import { ElementProps } from '@/components/sections/builder/types';
import { ImageGalleryElementDataType } from '@/components/sections/builder/elements/body/image-gallery/type';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { Checkbox } from '@/components/ui/checkbox';

import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ImageGalleryElementDataType;

  return (
    <div>
      <div className='mb-4 font-bold'>Content Alignment</div>
      <InputTab
        defaultValue={data.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />
      <div className='mb-4 mt-4 font-bold'>Heading Label</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setElementData(element.id, {
            headingLabel: value,
          })
        }
        value={data.headingLabel}
        placeholder='Label'
        className='mb-4 w-full max-w-64'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Heading Size</div>
      <InputTab
        defaultValue={data.headingSize}
        options={[
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            headingSize: v,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Text Label</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setElementData(element.id, {
            textLabel: value,
          })
        }
        value={data.textLabel}
        placeholder='Label'
        className='mb-4 w-full'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Text Style</div>
      <InputTab
        defaultValue={data.textStyle}
        options={[
          { label: 'Body', value: 'body' },
          { label: 'Subtitle', value: 'subtitle' },
          { label: 'UPPERCASE', value: 'uppercase' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            textStyle: v,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Layout</div>
      <InputTab
        defaultValue={data.layout}
        options={[
          { label: 'Grid', value: 'grid' },
          { label: 'Masonry', value: 'masonry' },
          { label: 'Carousel', value: 'carousel' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            layout: v,
          })
        }
      />
      {data.layout === 'carousel' && (
        <>
          <div className='mt-4 flex gap-x-1'>
            <Checkbox
              id='showIcon'
              className='mx-2'
              checked={data.showIcon}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  showIcon: !!checked,
                });
              }}
            />
            <label htmlFor='showIcon'>Show icons</label>
          </div>
          <div className='mb-2 mt-4 text-xs'>Change every</div>
          <div className='flex items-center'>
            <div className='w-full'>
              <InputSlider
                max={10}
                value={data.changeEvery || 0}
                onChange={(val) => {
                  builder.setElementData(element.id, {
                    changeEvery: val,
                  });
                }}
              />
            </div>
          </div>
        </>
      )}
      {(data.layout === 'grid' || data.layout === 'carousel') && (
        <>
          <div className='mb-2 mt-4 text-xs'>Image Ratio</div>
          <InputTab
            defaultValue={data.imageRatio}
            options={[
              { label: 'Square', value: 'aspect-square' },
              { label: '16:9', value: 'aspect-[16/9]' },
              { label: '9:16', value: 'aspect-[9/16]' },
            ]}
            onChange={(v) =>
              builder.setElementData(element.id, {
                imageRatio: v,
              })
            }
          />
          <div className='mb-2 mt-4 text-xs'>Image Size</div>
          <InputTab
            defaultValue={data.size}
            options={[
              { label: 'Small', value: 'small' },
              { label: 'Medium', value: 'medium' },
              { label: 'Large', value: 'large' },
            ]}
            onChange={(v) =>
              builder.setElementData(element.id, {
                size: v,
                totalCol: (() => {
                  if (v === 'small') return 4;
                  if (v === 'medium') return 3;
                  return 2;
                })(),
              })
            }
          />
          <div className='mb-2 mt-4 text-xs'>Number of columns on desktop</div>
          <InputSlider
            value={data.totalCol || 0}
            max={20}
            onChange={(val) =>
              builder.setElementData(element.id, {
                totalCol: val,
              })
            }
          />
        </>
      )}
      <div className='mb-2 mt-4'>Color scheme</div>
      <SchemeSetting
        value={data.sectionColorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            sectionColorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
        hideLeft
        hideRight
      />
      <BackgroundSetting
        value={data.background}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
    </div>
  );
}
